import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { usePlan } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/plan/index.tsx';
import { SidebarThemeProvider } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/sidebar-theme/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import ScenarioSwitcher from './scenario-switcher/index.tsx';
import { scenarioTheme } from './utils.tsx';

type Props = {
	submenuActive: boolean;
	children: ReactNode;
};

function PlanningContainer({ submenuActive, children }: Props) {
	const [{ match }] = useRouter();
	const { data } = usePlan();

	const {
		params: { scenarioId },
	} = match;

	const matchingScenario = data?.scenarios.find(({ id }) => String(id) === scenarioId);

	if (scenarioId == null || !matchingScenario || !data || submenuActive) {
		return null;
	}

	const { color } = matchingScenario;
	const { isMultiScenarioEnabled } = data;

	if (isMultiScenarioEnabled) {
		const theme = scenarioTheme[color] ?? scenarioTheme.default;
		return (
			<SidebarThemeProvider theme={{ navigation: { item: theme } }}>
				<MultiScenario backgroundColor={theme.background.default}>
					<ScenarioSwitcher
						planNavInfo={data}
						scenario={matchingScenario}
						outlineColor={theme.color.active}
					/>
					{children}
				</MultiScenario>
			</SidebarThemeProvider>
		);
	}

	return <>{children}</>;
}

export default PlanningContainer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MultiScenario = styled.div<{
	backgroundColor?: string;
}>({
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRadius: () => `${Math.round(borderRadius * 2.666)}px`,
	marginBottom: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.backgroundColor,
});
